import { degreeToRadian } from "common/utils/math";
import { aabb2ByMinXYWidthHeight } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { BSP_EPSILON } from "geom/bspTypes";
import { DIST_EPSILON, Line2Side } from "geom/core";
import type { HatchDesignMaterial } from "geom/hatchTypes";
import { hatchDesignMaterialNew } from "geom/hatchTypes";
import type { PolyLine2PolyLine2Intersection } from "geom/intersect";
import { intersectPolyLine3PolyLine3IgnoreZ, removeCoincidentPolyLine2Intersections } from "geom/intersect";
import type { CurveCurvePointIntersection, CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { offsetPolyLine2 } from "geom/offset";
import { planeNew } from "geom/plane";
import {
  polyLine2Combine,
  polyLine2Copy,
  polyLine2Explode,
  polyLine2FromAABB2,
  polyLine2IsEqual,
  polyLine2Move,
  polyLine2NonUniformScale,
  polyLine2Rotate,
  polyLine2Simplify,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3AtZ,
  polyLine3ByArray,
  polyLine3Copy,
  polyLine3Move,
  polyLine3NonUniformScale,
  polyLine3OnPlane,
  polyLine3RotateZ,
  polyLine3ToPolyLine2IgnoreZ,
} from "geom/polyLine3";
import { ray2New, ray2Side } from "geom/ray2";
import type { Ray2 } from "geom/ray2Types";
import { vec2Dist } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { CONTINUOUS_HATCH_PATTERN_PERIOD } from "gfx/materials/designMaterialDefaults";
import {
  cellTopDownBoundingBoxX,
  cellTopDownBoundingBoxY,
  normalizeSdfPatternRotation,
  sdfPeriod,
  SdfPeriodPurpose,
} from "gfx/materials/designMaterialParams";
import type {
  DesignMaterialParamsPreprocessed,
  SdfCommonParamsPreprocessed,
  SdfParamsPreprocessed,
  SdfPatternPreprocessed,
  SdfRandomTilesParams,
} from "gfx/materials/designMaterialTypes";
import { generateCellOutline } from "gfx/materials/discretization/generateCellOutline";
import { sdfCellBoundariesInAABB } from "gfx/materials/discretization/sdfLayout";

export type HatchDesignMaterialParams = Pick<
  DesignMaterialParamsPreprocessed,
  | "hatchMaterialId"
  | "sdf"
  | "heightMapEnabled"
  | "heightMapName"
  | "hatchMaterialIgnoreVaryHeight"
  | "hatchMaterialIgnoreBevels"
>;

export function copyHatchDesignMaterialParams(params: DesignMaterialParamsPreprocessed): HatchDesignMaterialParams {
  return {
    hatchMaterialId: params.hatchMaterialId,
    sdf: params.sdf,
    heightMapEnabled: params.heightMapEnabled,
    heightMapName: params.heightMapName,
    hatchMaterialIgnoreVaryHeight: params.hatchMaterialIgnoreVaryHeight,
    hatchMaterialIgnoreBevels: params.hatchMaterialIgnoreBevels,
  };
}

export function hatchMaterialFromDesignMaterial(
  params: HatchDesignMaterialParams,
  forHatchSDFTexture: boolean = false,
): HatchDesignMaterial {
  const sdfParams: SdfParamsPreprocessed = params?.sdf;

  if (
    !sdfParams ||
    (sdfParams.common.maskOnly && !sdfParams.common.autoGenerateHatchIfMaskOnly) ||
    (params.heightMapEnabled && params.heightMapName)
  ) {
    return hatchDesignMaterialNew(100, 100);
  }

  // get the normalized patterns and their period
  const { normalizedPatterns, rotation: baseRotation } = normalizeSdfPatternRotation(
    sdfParams.common,
    sdfParams.patterns,
  );

  for (const pattern of normalizedPatterns) {
    if (params.hatchMaterialIgnoreVaryHeight) {
      pattern.varyHeight = false;
      pattern.varyHeightScale = 0.0;
    }
    if (params.hatchMaterialIgnoreBevels) {
      pattern.bevelDistanceX = 0.0;
      pattern.bevelDistanceY = 0.0;
      pattern.bevelDistanceZ = 0.0;
    }
    if (pattern.continuousX) {
      // This number needs to be sufficiently large compared to the period so that
      // the edges of the cell in the continuous direction will be clipped out.
      pattern.xSize = CONTINUOUS_HATCH_PATTERN_PERIOD * 3;

      pattern.xOffset -= pattern.xSize * 0.5;
      pattern.xDistance = pattern.xSize;
    } else if (pattern.continuousY) {
      pattern.ySize = CONTINUOUS_HATCH_PATTERN_PERIOD * 3;
      pattern.yOffset -= pattern.ySize * 0.5;
      pattern.yDistance = pattern.ySize;
    }
  }

  const { repeatPeriodX: sizeU, repeatPeriodY: sizeV } = sdfPeriod(
    sdfParams.common,
    normalizedPatterns,
    forHatchSDFTexture ? SdfPeriodPurpose.HatchTexturePeriod : SdfPeriodPurpose.HatchPeriod,
  );

  return hatchMaterialFromDesignMaterial2D(
    sdfParams.common,
    sdfParams.randomTiles,
    normalizedPatterns,
    sizeU ?? 1,
    sizeV ?? 1,
    baseRotation ?? 0,
  );
}

function polyLine2RayIntersectionCompare(a: Ray2PolyLine2Intersection, b: Ray2PolyLine2Intersection) {
  if (a.edgeIndex > b.edgeIndex) {
    return 1;
  }
  if (a.edgeIndex < b.edgeIndex) {
    return -1;
  }
  return a.lineT > b.lineT ? 1 : -1;
}

function simplifyLinesInRepeatingPattern(inputLines: PolyLine2[], sizeU: number, sizeV: number): PolyLine2[] {
  const linesExploded: PolyLine2[] = [];
  for (const pl of inputLines) {
    linesExploded.push(...polyLine2Explode(pl));
  }
  const insideLines: PolyLine2[] = [];
  const linesAtEdges: PolyLine2[] = [];
  for (const pl of linesExploded) {
    if (
      pl[0] > DIST_EPSILON &&
      pl[1] > DIST_EPSILON &&
      pl[0] < sizeU - DIST_EPSILON &&
      pl[1] < sizeV - DIST_EPSILON &&
      pl[2] > DIST_EPSILON &&
      pl[3] > DIST_EPSILON &&
      pl[2] < sizeU - DIST_EPSILON &&
      pl[3] < sizeV - DIST_EPSILON
    ) {
      // Line is fully inside the AABB.
      insideLines.push(pl);
    } else {
      if (pl[0] >= sizeU - DIST_EPSILON || pl[2] >= sizeU - DIST_EPSILON) {
        pl[0] -= sizeU;
        pl[2] -= sizeU;
      }
      if (pl[1] >= sizeV - DIST_EPSILON || pl[3] >= sizeV - DIST_EPSILON) {
        pl[1] -= sizeV;
        pl[3] -= sizeV;
      }
      if (pl[2] < pl[0] || (pl[2] === pl[0] && pl[3] < pl[1])) {
        // Normalize the order of vertices
        const tempX = pl[0];
        const tempY = pl[1];
        pl[0] = pl[2];
        pl[1] = pl[3];
        pl[2] = tempX;
        pl[3] = tempY;
      }
      let alreadyAdded = false;
      for (let i = 0; i < linesAtEdges.length; ++i) {
        // Check if we already have a copy of the same line.
        if (polyLine2IsEqual(pl, linesAtEdges[i], 1e-3)) {
          alreadyAdded = true;
          break;
        }
      }
      if (!alreadyAdded) {
        linesAtEdges.push(pl);
      }
    }
  }

  // Combine lines so that we can remove duplicate vertices and collinear segments.
  let linesWithoutDuplicates: PolyLine2[] = [...insideLines, ...linesAtEdges];
  linesWithoutDuplicates = polyLine2Combine(linesWithoutDuplicates, 0.01);
  for (let i = 0, l = linesWithoutDuplicates.length; i < l; ++i) {
    linesWithoutDuplicates[i] = polyLine2Simplify(linesWithoutDuplicates[i], 0.01, 0.01);
  }
  return linesWithoutDuplicates;
}

function ensureLinesCrossTileAABBOnlyOnPositiveSide(lines: PolyLine2[], sizeU: number, sizeV: number): PolyLine2[] {
  // Explode again and move lines so that they can only cross the AABB edges on one side.
  // This makes sure that hatch material fill doesn't miss any lines.
  const resultLines: PolyLine2[] = [];
  for (const pl of lines) {
    resultLines.push(...polyLine2Explode(pl));
  }

  for (let i = 0, l = resultLines.length; i < l; ++i) {
    const pl = resultLines[i];
    if (pl[0] < 0 || pl[2] < 0) {
      polyLine2Move(resultLines[i], sizeU, 0);
    }
    if (pl[1] < 0 || pl[3] < 0) {
      polyLine2Move(resultLines[i], 0, sizeV);
    }
  }

  return resultLines;
}

/** Will cut out the parts of the lines that are on the left side of the ray. */
function clipLines(lines: PolyLine2[], ray: Ray2): PolyLine2[] {
  const clippedLines: PolyLine2[] = [];
  for (const pl of lines) {
    const intersections = intersectRay2PolyLine2(pl, ray.originX, ray.originY, ray.dirX, ray.dirY);

    if (intersections.length === 0) {
      if (ray2Side(ray.originX, ray.originY, ray.dirX, ray.dirY, pl[0], pl[1]) === Line2Side.RIGHT) {
        clippedLines.push(pl);
      }
      continue;
    }

    intersections.sort(polyLine2RayIntersectionCompare);

    let clippedLine: number[] = [];

    let intersectionIndex = 0;
    let sourcePointIndex = 0;
    const intersection0 = intersections[0];
    const edgeIndex0 = intersection0.edgeIndex;
    const beforeIntersectionX = pl[edgeIndex0 * 2];
    const beforeIntersectionY = pl[edgeIndex0 * 2 + 1];
    const pointBeforeSide = ray2Side(
      ray.originX,
      ray.originY,
      ray.dirX,
      ray.dirY,
      beforeIntersectionX,
      beforeIntersectionY,
    );
    if (pointBeforeSide === Line2Side.LEFT) {
      // Cut the points before the first intersection
      clippedLine.push(intersection0.x);
      clippedLine.push(intersection0.y);
      sourcePointIndex = edgeIndex0 + 1;
      ++intersectionIndex;
    }
    let segmentIncluded = true;
    while (intersectionIndex < intersections.length) {
      const intersection = intersections[intersectionIndex];
      const edgeIndex = intersection.edgeIndex;
      if (segmentIncluded) {
        while (sourcePointIndex <= edgeIndex) {
          clippedLine.push(pl[sourcePointIndex * 2], pl[sourcePointIndex * 2 + 1]);
          ++sourcePointIndex;
        }
        clippedLine.push(intersection.x);
        clippedLine.push(intersection.y);
        clippedLines.push(polyLine2New(clippedLine));
        clippedLine = [];
      } else {
        clippedLine.push(intersection.x);
        clippedLine.push(intersection.y);
        sourcePointIndex = edgeIndex + 1;
      }
      segmentIncluded = !segmentIncluded;
      ++intersectionIndex;
    }
    if (segmentIncluded) {
      while (sourcePointIndex < pl.length / 2) {
        clippedLine.push(pl[sourcePointIndex * 2], pl[sourcePointIndex * 2 + 1]);
        ++sourcePointIndex;
      }
      clippedLines.push(polyLine2New(clippedLine));
    }
  }
  return clippedLines;
}

function getIntersectionZ0(int: PolyLine2PolyLine2Intersection, pl: PolyLine3) {
  const segmentLen = vec2Dist(
    pl[int.edgeIndexA * 3],
    pl[int.edgeIndexA * 3 + 1],
    pl[(int.edgeIndexA + 1) * 3],
    pl[(int.edgeIndexA + 1) * 3 + 1],
  );
  let t;
  if ((int.int as CurveCurvePointIntersection).param0 !== undefined) {
    t = (int.int as CurveCurvePointIntersection).param0;
  } else {
    t = (int.int as CurveCurveRangeIntersection).startParam0;
  }
  t /= segmentLen;
  return pl[int.edgeIndexA * 3 + 2] * (1 - t) + pl[(int.edgeIndexA + 1) * 3 + 2] * t;
}

function getIntersectionZ1(int: PolyLine2PolyLine2Intersection, pl: PolyLine3) {
  const segmentLen = vec2Dist(
    pl[int.edgeIndexB * 3],
    pl[int.edgeIndexB * 3 + 1],
    pl[(int.edgeIndexB + 1) * 3],
    pl[(int.edgeIndexB + 1) * 3 + 1],
  );
  let t;
  if ((int.int as CurveCurvePointIntersection).param1 !== undefined) {
    t = (int.int as CurveCurvePointIntersection).param1;
  } else {
    t = (int.int as CurveCurveRangeIntersection).startParam1;
  }
  t /= segmentLen;
  return pl[int.edgeIndexB * 3 + 2] * (1 - t) + pl[(int.edgeIndexB + 1) * 3 + 2] * t;
}

function getIntersectionClipXYZ(int: PolyLine2PolyLine2Intersection, pl: PolyLine3): Vec3 {
  const segmentLen = vec2Dist(
    pl[int.edgeIndexA * 3],
    pl[int.edgeIndexA * 3 + 1],
    pl[(int.edgeIndexA + 1) * 3],
    pl[(int.edgeIndexA + 1) * 3 + 1],
  );
  if ((int.int as CurveCurvePointIntersection).x !== undefined) {
    const t = (int.int as CurveCurvePointIntersection).param0 / segmentLen;
    const z = pl[int.edgeIndexA * 3 + 2] * (1 - t) + pl[(int.edgeIndexA + 1) * 3 + 2] * t;
    return [(int.int as CurveCurvePointIntersection).x, (int.int as CurveCurvePointIntersection).y, z];
  }
  const t = (int.int as CurveCurveRangeIntersection).startParam0 / segmentLen;
  const z = pl[int.edgeIndexA * 3 + 2] * (1 - t) + pl[(int.edgeIndexA + 1) * 3 + 2] * t;
  return [(int.int as CurveCurveRangeIntersection).startx, (int.int as CurveCurveRangeIntersection).starty, z];
}

function polyLine2IntersectionCompare(a: PolyLine2PolyLine2Intersection, b: PolyLine2PolyLine2Intersection) {
  if (a.edgeIndexA > b.edgeIndexA) {
    return 1;
  }
  if (a.edgeIndexA < b.edgeIndexA) {
    return -1;
  }
  let tA, tB;
  if ((a.int as CurveCurvePointIntersection).param0 !== undefined) {
    tA = (a.int as CurveCurvePointIntersection).param0;
  } else {
    tA = (a.int as CurveCurveRangeIntersection).startParam0;
  }
  if ((b.int as CurveCurvePointIntersection).param0 !== undefined) {
    tB = (b.int as CurveCurvePointIntersection).param0;
  } else {
    tB = (b.int as CurveCurveRangeIntersection).startParam0;
  }
  return tA > tB ? 1 : -1;
}

/** Will cut out the parts of the lines that are obscured by obscuringLine (with fixed winding order).
 * Larger z values get obscured by smaller ones.
 * z values that are roughly the same will also clip - effectively joining together overlapping shapes on the same plane. */
function removeHiddenLines(lines: PolyLine3[], obscuringLine: PolyLine3): PolyLine3[] {
  const clippedLines: PolyLine3[] = [];

  for (const pl of lines) {
    const intersections = removeCoincidentPolyLine2Intersections(intersectPolyLine3PolyLine3IgnoreZ(pl, obscuringLine));
    if (intersections.length === 0) {
      clippedLines.push(pl);
      continue;
    }
    let clip = true;
    for (const int of intersections) {
      const resultZ = getIntersectionZ0(int, pl);
      const obscuringZ = getIntersectionZ1(int, obscuringLine);
      const DEPTH_TEST_TOLERANCE = 0.005;
      if (resultZ < obscuringZ - DEPTH_TEST_TOLERANCE) {
        // Clip if resultZ is behind obscuringZ or if it's roughly the same.
        // Clipping when the z values are roughly the same will make more complex shapes made out of multiple patterns look continuous.
        clip = false;
      }
    }
    if (!clip) {
      clippedLines.push(pl);
      continue;
    }

    intersections.sort(polyLine2IntersectionCompare);

    let clippedLine: number[] = [];

    let intersectionIndex = 0;
    let sourcePointIndex = 0;
    const intersection0 = intersections[0];
    const edgeIndex0 = intersection0.edgeIndexA;
    const beforeIntersectionX = pl[edgeIndex0 * 3];
    const beforeIntersectionY = pl[edgeIndex0 * 3 + 1];
    const pointBeforeSide = ray2Side(
      obscuringLine[intersection0.edgeIndexB * 3],
      obscuringLine[intersection0.edgeIndexB * 3 + 1],
      obscuringLine[(intersection0.edgeIndexB + 1) * 3] - obscuringLine[intersection0.edgeIndexB * 3],
      obscuringLine[(intersection0.edgeIndexB + 1) * 3 + 1] - obscuringLine[intersection0.edgeIndexB * 3 + 1],
      beforeIntersectionX,
      beforeIntersectionY,
    );
    if (pointBeforeSide === Line2Side.RIGHT) {
      // Note that this part requires that the winding order of obscuringLine is fixed.
      // Cut the points before the first intersection.
      const [intersection0X, intersection0Y, intersection0Z] = getIntersectionClipXYZ(intersection0, pl);
      clippedLine.push(intersection0X);
      clippedLine.push(intersection0Y);
      clippedLine.push(intersection0Z);
      sourcePointIndex = edgeIndex0 + 1;
      ++intersectionIndex;
    }
    let segmentIncluded = true;
    while (intersectionIndex < intersections.length) {
      const intersection = intersections[intersectionIndex];
      const [intersectionX, intersectionY, intersectionZ] = getIntersectionClipXYZ(intersection, pl);
      const edgeIndex = intersection.edgeIndexA;
      if (segmentIncluded) {
        while (sourcePointIndex <= edgeIndex) {
          clippedLine.push(pl[sourcePointIndex * 3], pl[sourcePointIndex * 3 + 1], pl[sourcePointIndex * 3 + 2]);
          ++sourcePointIndex;
        }
        clippedLine.push(intersectionX);
        clippedLine.push(intersectionY);
        clippedLine.push(intersectionZ);
        clippedLines.push(polyLine3ByArray(clippedLine));
        clippedLine = [];
      } else {
        clippedLine.push(intersectionX);
        clippedLine.push(intersectionY);
        clippedLine.push(intersectionZ);
        sourcePointIndex = edgeIndex + 1;
      }
      segmentIncluded = !segmentIncluded;
      ++intersectionIndex;
    }
    if (segmentIncluded) {
      while (sourcePointIndex < pl.length / 3) {
        clippedLine.push(pl[sourcePointIndex * 3], pl[sourcePointIndex * 3 + 1], pl[sourcePointIndex * 3 + 2]);
        ++sourcePointIndex;
      }
      clippedLines.push(polyLine3ByArray(clippedLine));
      clippedLine = [];
    }
  }
  return clippedLines;
}

function minDistanceAlongAxis(offsetA, offsetB, repeatInterval) {
  const diff = Math.abs(offsetA - offsetB) % repeatInterval;
  return Math.min(diff, repeatInterval - diff);
}

/**
 * @param pl Polyline to clip. Polyline should be a closed polyline on a single plane.
 * @param maxZ Points greater than maxZ will get clipped out
 * @returns Clipped polyline. This will be a closed polyline as well.
 */
function clipPolyLine3Z(pl: PolyLine3, maxZ: number): PolyLine3 | null {
  let numClipped = 0;
  for (let i = 0, l = pl.length / 3; i < l; ++i) {
    if (pl[i * 3 + 2] > maxZ) {
      ++numClipped;
    }
  }
  if (numClipped === 0) {
    return pl;
  }
  if (numClipped === pl.length / 3) {
    return null;
  }
  const clippedLine: number[] = [];
  const firstPointClipped = pl[2] > maxZ;
  if (!firstPointClipped) {
    clippedLine.push(pl[0], pl[1], pl[2]);
  }
  let prevPointClipped = firstPointClipped;
  for (let i = 1, l = pl.length / 3; i < l; ++i) {
    const pointClipped = pl[i * 3 + 2] > maxZ;
    if (prevPointClipped && !pointClipped) {
      const prevZ = pl[(i - 1) * 3 + 2];
      const currZ = pl[i * 3 + 2];
      const t = 1 - (maxZ - currZ) / (prevZ - currZ);
      clippedLine.push(pl[i * 3] * t + pl[(i - 1) * 3] * (1 - t));
      clippedLine.push(pl[i * 3 + 1] * t + pl[(i - 1) * 3 + 1] * (1 - t));
      clippedLine.push(maxZ);
    }
    if (!pointClipped) {
      clippedLine.push(pl[i * 3], pl[i * 3 + 1], pl[i * 3 + 2]);
    }
    if (pointClipped && !prevPointClipped) {
      const prevZ = pl[(i - 1) * 3 + 2];
      const currZ = pl[i * 3 + 2];
      const t = (maxZ - prevZ) / (currZ - prevZ);
      clippedLine.push(pl[i * 3] * t + pl[(i - 1) * 3] * (1 - t));
      clippedLine.push(pl[i * 3 + 1] * t + pl[(i - 1) * 3 + 1] * (1 - t));
      clippedLine.push(maxZ);
    }
    prevPointClipped = pointClipped;
  }
  if (firstPointClipped) {
    // Close the polyline
    clippedLine.push(clippedLine[0], clippedLine[1], clippedLine[2]);
  }
  return polyLine3ByArray(clippedLine);
}

function polyline3PartIsInsideAABB2Inclusive(pl: PolyLine3, aabb: AABB2) {
  const ptsCount = pl.length / 3;
  for (let i = 0; i < ptsCount; ++i) {
    const x = pl[i * 3];
    const y = pl[i * 3 + 1];
    if (x >= aabb.minX && x <= aabb.maxX && y >= aabb.minY && y <= aabb.maxY) {
      return true;
    }
  }
  const aabbPl = polyLine2FromAABB2(aabb);
  const aabbPl3 = polyLine3AtZ(aabbPl, 0);
  return intersectPolyLine3PolyLine3IgnoreZ(pl, aabbPl3).length > 0;
}

function hatchMaterialFromDesignMaterial2D(
  sdfCommon: SdfCommonParamsPreprocessed,
  sdfRandomTiles: SdfRandomTilesParams,
  patterns: SdfPatternPreprocessed[],
  sizeU: number,
  sizeV: number,
  baseRotation: number,
): HatchDesignMaterial {
  // The alignment does NOT MATCH the legacy BSP based hatch generation.

  let lines: PolyLine2[] = [];

  interface Line3D {
    line: PolyLine3;
    contractObscuringBeveledShape: boolean; // The bevels on the original SDF shape are not taken into account when generating its outline. If the shape is beveled, contract the outline slightly when it's used to occlude other lines.
  }

  const lines3D: Line3D[] = [];
  let patternIndex = 0;
  const uvAABB = aabb2ByMinXYWidthHeight(0, 0, sizeU, sizeV);
  const slopeZ = sdfCommon.slopeAxis === "Z" && sdfCommon.randomTilesMode === "None";
  const slopeX = sdfCommon.slopeAxis === "X";

  const BEVEL_EPSILON = 0.025;
  const EXPAND_OBSCURING_LINE_EPSILON = 0.035;

  function bevelAffectsOcclusionSize(pattern: SdfPatternPreprocessed) {
    return (
      (pattern.useBevels && (pattern.bevelDistanceY > 0 || pattern.bevelDistanceX > 0)) ||
      ((pattern.roundSidesMode === "roundSides" ||
        pattern.roundSidesMode === "roundY" ||
        pattern.roundSidesMode === "roundX") &&
        pattern.roundStrength > 0)
    );
  }

  // Conservatively detect overlapping patterns.
  let possiblyOverlappingPatterns = false;
  let clipAgainstBackplane = false;
  const patternCount = patterns.length;
  for (let patternIndex = 0; patternIndex < patternCount; ++patternIndex) {
    const pattern: SdfPatternPreprocessed = patterns[patternIndex];
    const xSize = cellTopDownBoundingBoxX(pattern) - (bevelAffectsOcclusionSize(pattern) ? BEVEL_EPSILON : 0);
    const ySize =
      cellTopDownBoundingBoxY(sdfCommon, pattern) - (bevelAffectsOcclusionSize(pattern) ? BEVEL_EPSILON : 0);

    if (pattern.slope !== 0 && slopeX) {
      clipAgainstBackplane = true;
    }
    if (pattern.slope !== 0 && slopeZ) {
      possiblyOverlappingPatterns = true;
    }
    for (let patternIndexB = patternIndex + 1; patternIndexB < patternCount; ++patternIndexB) {
      const patternB = patterns[patternIndexB];
      if (pattern.rotation !== patternB.rotation) {
        possiblyOverlappingPatterns = true;
      }

      const xSizeB = cellTopDownBoundingBoxX(patternB) - (bevelAffectsOcclusionSize(patternB) ? BEVEL_EPSILON : 0);
      const ySizeB =
        cellTopDownBoundingBoxY(sdfCommon, patternB) - (bevelAffectsOcclusionSize(patternB) ? BEVEL_EPSILON : 0);

      const DISTANCE_VARIATION_TOLERANCE = 0.01;
      if (
        (Math.abs(patternB.xDistance - pattern.xDistance) > DISTANCE_VARIATION_TOLERANCE ||
          minDistanceAlongAxis(patternB.xOffset, pattern.xOffset, pattern.xDistance) <= (xSize + xSizeB) * 0.5) &&
        (Math.abs(patternB.yDistance - pattern.yDistance) > DISTANCE_VARIATION_TOLERANCE ||
          minDistanceAlongAxis(patternB.yOffset, pattern.yOffset, pattern.yDistance) <= (ySize + ySizeB) * 0.5)
      ) {
        possiblyOverlappingPatterns = true;
      }
    }
  }

  // Depth test will run whenever we have overlapping patterns even if all patterns are on the same z plane,
  // so we can join together more complex shapes made up by more than one overlapping pattern.
  const depthTest = possiblyOverlappingPatterns;
  for (const pattern of patterns) {
    const slopeForZOffset = degreeToRadian(slopeX ? pattern.slope : 0);
    // Each SDF tile uses the corner closest to the surface as its pivot point when applying slope rotation along the X axis.
    const zOffset =
      sdfCommon.depth -
      pattern.zSize +
      Math.sin(Math.abs(slopeForZOffset)) * Math.min(pattern.ySize, pattern.yDistance) * 0.5;
    const cellRotation = pattern.rotation + (slopeZ ? pattern.slope : 0);
    const cellOutline = generateCellOutline(pattern, 15);
    if (cellOutline === null) {
      ++patternIndex;
      continue;
    }
    const cellLocations: Generator<number[]> = sdfCellBoundariesInAABB(
      sdfCommon,
      sdfRandomTiles,
      pattern,
      patternIndex,
      uvAABB,
      true,
      sizeU,
      sizeV,
    );
    let cellLocation = cellLocations.next();

    if (clipAgainstBackplane || depthTest) {
      const plane = planeNew(0, 0, 0, 1, 0, 0, 0, Math.cos(slopeForZOffset), -Math.sin(slopeForZOffset));
      const cellOutline3D = polyLine3OnPlane(cellOutline, plane);
      while (!cellLocation.done) {
        const [centerx, centery, width, height] = cellLocation.value as number[];
        cellLocation = cellLocations.next();
        const cellInstance = polyLine3Copy(cellOutline3D);
        polyLine3NonUniformScale(cellInstance, width / pattern.xSize, height / pattern.ySize, 1);
        polyLine3RotateZ(cellInstance, degreeToRadian(cellRotation));

        // Each SDF tile uses the corner closest to the surface as its pivot point when applying slope rotation along the X axis.
        // However, we rotate that plane around the center point of the tile when generating the cell outline. Correct for this.
        const pivotYCorrection =
          Math.sign(slopeForZOffset) *
          (1.0 - Math.cos(slopeForZOffset)) *
          Math.min(pattern.ySize, pattern.yDistance) *
          0.5;

        polyLine3Move(cellInstance, centerx, centery + pivotYCorrection, zOffset);

        // The conservative cell location algorithm can generate a lot of extra cells, exclude the ones that don't have any points inside the AABB.
        if (!polyline3PartIsInsideAABB2Inclusive(cellInstance, uvAABB)) {
          continue;
        }

        // Clip the line against the SDF backplane. This can affect the shape of sloping patterns.
        const clippedLine3D = clipPolyLine3Z(cellInstance, sdfCommon.depth);
        if (clippedLine3D) {
          if (depthTest) {
            lines3D.push({ line: clippedLine3D, contractObscuringBeveledShape: bevelAffectsOcclusionSize(pattern) });
          } else {
            lines.push(polyLine3ToPolyLine2IgnoreZ(clippedLine3D));
          }
        }
      }
    } else {
      while (!cellLocation.done) {
        const [centerx, centery, width, height] = cellLocation.value as number[];
        cellLocation = cellLocations.next();
        const cellInstance = polyLine2Copy(cellOutline);
        polyLine2NonUniformScale(cellInstance, width / pattern.xSize, height / pattern.ySize);
        polyLine2Rotate(cellInstance, degreeToRadian(cellRotation));
        polyLine2Move(cellInstance, centerx, centery);
        lines.push(cellInstance);
      }
    }

    ++patternIndex;
  }

  if (depthTest) {
    // Generate an expanded version of every line for clipping in order to avoid
    // degenerate cases of the Greiner-Hormann style clipping algorithm we use.
    // Roof_AsphaltBelmont and Roof_AsphaltShake are good test cases for this.
    const obscuringLines: PolyLine2[] = [];
    for (let lineI = 0, l = lines3D.length; lineI < l; ++lineI) {
      const contractionDistance = lines3D[lineI].contractObscuringBeveledShape
        ? BEVEL_EPSILON
        : -EXPAND_OBSCURING_LINE_EPSILON;
      const obscuringLine = polyLine3AtZ(
        offsetPolyLine2(polyLine3ToPolyLine2IgnoreZ(lines3D[lineI].line), contractionDistance),
        0.0,
      );
      // Copy z values from original line
      for (let pointI = 0; pointI < obscuringLine.length / 3; ++pointI) {
        obscuringLine[pointI * 3 + 2] = lines3D[lineI].line[pointI * 3 + 2];
      }
      obscuringLines.push(obscuringLine);
    }
    for (let i = 0, l = lines3D.length; i < l; ++i) {
      let resultingLines3D: PolyLine3[] = [lines3D[i].line];
      for (let j = 0; j < l; ++j) {
        if (j === i) {
          continue;
        }
        const obscuringLine = obscuringLines[j];
        resultingLines3D = removeHiddenLines(resultingLines3D, obscuringLine);
      }
      for (const line3D of resultingLines3D) {
        lines.push(polyLine3ToPolyLine2IgnoreZ(line3D));
      }
    }
  }

  const clipRays = [
    ray2New(-2 * BSP_EPSILON, -2 * BSP_EPSILON, 0, 1),
    ray2New(-2 * BSP_EPSILON, sizeV + 2 * BSP_EPSILON, 1, 0),
    ray2New(sizeU + 2 * BSP_EPSILON, sizeV + 2 * BSP_EPSILON, 0, -1),
    ray2New(sizeU + 2 * BSP_EPSILON, -2 * BSP_EPSILON, -1, 0),
  ];

  for (const clipRay of clipRays) {
    lines = clipLines(lines, clipRay);
  }

  const linesWithoutDuplicates = simplifyLinesInRepeatingPattern(lines, sizeU, sizeV);
  const finalLines = ensureLinesCrossTileAABBOnlyOnPositiveSide(linesWithoutDuplicates, sizeU, sizeV);

  return hatchDesignMaterialNew(sizeU, sizeV, baseRotation, finalLines);
}
